import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { switchMap, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/**
 * Interface for JWT user data claims that may contain user ID
 */
interface UserDataClaims {
  'custom:database_id'?: string | number;
  'custom:user_id'?: string | number;
  sub?: string;
  [key: string]: unknown; // Allow other claims with unknown type
}

/**
 * HTTP interceptor that adds authentication headers to all API requests
 * - In all environments: prefers Bearer token authentication
 * - In development only: falls back to x-user-id header if no Bearer token is available
 * - In production: requires Bearer token (no x-user-id fallback)
 */
export const authHeaderInterceptor: HttpInterceptorFn = (req, next) => {
  const oidcSecurityService = inject(OidcSecurityService);

  // Only add header for API requests (not for external resources)
  let isApiRequest = false;
  try {
    const url = new URL(req.url, window.location.origin);
    isApiRequest = url.pathname.startsWith('/api/');
  } catch {
    isApiRequest = req.url.startsWith('/api/');
  }

  if (!isApiRequest) {
    return next(req);
  }

  // Prefer Bearer token auth (required by backend middleware)
  return oidcSecurityService.getAccessToken().pipe(
    take(1),
    switchMap((accessToken) => {
      if (accessToken) {
        return next(
          req.clone({
            setHeaders: {
              Authorization: `Bearer ${accessToken}`,
            },
          })
        );
      }

      // Development-only fallback: use x-user-id header if available
      // In production, this fallback is skipped to match backend behavior
      if (!environment.production) {
        return oidcSecurityService.userData$.pipe(
          take(1),
          switchMap((userData) => {
            if (userData) {
              const userDataObj = userData as unknown as UserDataClaims;
              const userId =
                userDataObj['custom:database_id'] || userDataObj['custom:user_id'] || userDataObj['sub'];

              if (userId) {
                return next(
                  req.clone({
                    setHeaders: {
                      'x-user-id': String(userId),
                    },
                  })
                );
              }
            }

            return next(req);
          })
        );
      }

      // In production, proceed without any auth headers if no Bearer token
      return next(req);
    })
  );
};
